import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import LectureCard from '../components/LectureCard';
import FloatingNavButtons from '../components/FloatingNavButtons';
import { fetchLectureData } from '../services/googleSheets';
import theme from '../styles/theme';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import FloatingShareButton from '../components/FloatingShareButton';
import '../styles/global.css';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * HomePage component to display a list of available lectures
 * @returns {JSX.Element} HomePage component
 */
const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config, loading: configLoading, error: configError } = useConfig();
  const isMobileView = useMediaQuery('(max-width: 768px)');
  
  const [lectures, setLectures] = useState([]);
  const [filteredLectures, setFilteredLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  
  // Extract search query from URL if present
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';
  const eventFilter = searchParams.get('event') || '';
  
  // Fetch lecture data when component mounts or config changes
  useEffect(() => {
    const loadLectureData = async () => {
      // Wait for config
      if (configLoading || !config) {
        console.log('HomePage: Waiting for config...');
        return; 
      }
      // Handle config error
      if (configError) {
        console.error('HomePage: Config loading failed:', configError);
        setError(`Failed to load configuration: ${configError.message || configError}`);
        setLoading(false);
        return;
      }
      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('HomePage: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in the configuration.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log(`HomePage: Fetching data for sheet ID: ${config.google_sheet_id}`);
        // Try to fetch data, but don't let errors break the page
        let lectureData = [];
        try {
          lectureData = await fetchLectureData(config.google_sheet_id);
          console.log('Raw lecture data:', lectureData);
        } catch (fetchError) {
          console.error('Error fetching data:', fetchError);
          // Don't rethrow - just continue with empty data
        }
        
        // If no data or empty array, use fallback events
        if (!Array.isArray(lectureData) || lectureData.length === 0) {
          console.log('No lecture data found - using fallback events');
          setEvents(['Ramadan 2025', 'Muharram 2024']);
          setLoading(false);
          return;
        }
        
        // Save the lecture data
        setLectures(lectureData);
        
        // Extract unique events from Column A (event property)
        const rawEvents = lectureData
          .map(lecture => lecture.event || '')
          .filter(event => event && event.trim() !== ''); // Remove empty events
          
        console.log('Raw events extracted:', rawEvents);

        // If no events found after extraction, use fallback
        if (rawEvents.length === 0) {
          console.log('No events extracted - using fallback events');
          setEvents(['Ramadan 2025', 'Muharram 2024']);
          setLoading(false);
          return;
        }
        
        // Get unique events
        const uniqueEvents = [...new Set(rawEvents.map(e => e.trim()))];
        console.log('Unique events:', uniqueEvents);
        
        // Sort events with Ramadan 2025 first, then alphabetical
        const sortedEvents = uniqueEvents.sort((a, b) => {
          if (a.toLowerCase().includes('ramadan')) return -1;
          if (b.toLowerCase().includes('ramadan')) return 1;
          return a.localeCompare(b);
        });

        setEvents(sortedEvents);
        setLoading(false);
      } catch (err) {
        console.error('Error in event processing:', err);
        setError('Failed to process events. Showing fallback content.');
        setEvents(['Ramadan 2025', 'Muharram 2024']); // Guaranteed fallback
        setLoading(false);
      }
    };
    
    loadLectureData();
  }, [config, configLoading, configError, searchQuery, eventFilter]);
  
  // Group lectures by event and sort them
  const groupLecturesByEvent = (lectures) => {
    return lectures.reduce((groups, lecture) => {
      const event = lecture.event || 'Uncategorized';
      if (!groups[event]) {
        groups[event] = [];
      }
      groups[event].push(lecture);
      return groups;
    }, {});
  };
  
  const lecturesByEvent = groupLecturesByEvent(lectures);
  console.log('Grouped by event:', lecturesByEvent);
  
  // Sort lectures within each event by date (newest first)
  Object.keys(lecturesByEvent).forEach(event => {
    lecturesByEvent[event].sort((a, b) => {
      const dateA = new Date(a.date || '2000-01-01');
      const dateB = new Date(b.date || '2000-01-01');
      return dateB - dateA;
    });
  });
  
  console.log('First Lecture Object:', lectures[0]);

  // Determine which layout to use based on device type
  const Layout = isMobile() ? MobileLayout : DesktopLayout;

  // Handle config loading/error state before rendering
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }
  
  return (
    <Layout>
      <div className="home-page" style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '30px', 
          textAlign: 'center',
          padding: '20px 0'
        }}>
          <h1 style={{ 
            fontSize: isMobileView ? '1.75rem' : '2.5rem', 
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            marginBottom: '10px',
            padding: isMobileView ? '0 10px' : '0'
          }}>
            {config.ORGANIZATION.NAME}
          </h1>
          <p style={{ 
            fontSize: isMobileView ? '1rem' : '1.2rem', 
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            maxWidth: '800px',
            margin: '0 auto',
            padding: isMobileView ? '0 15px' : '0'
          }}>
            Browse our lecture series and events
          </p>
        </div>

        {/* Content Area */}
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h2>Loading Events...</h2>
            </div>
          )}
          
          {/* Error State */}
          {!loading && error && (
            <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
              <h2>Error</h2>
              <p>{error}</p>
            </div>
          )}
          
          {/* Events List */}
          {!loading && !error && events.length > 0 && (
            <div className="events-list" style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '30px'
            }}>
              {events.map((event, index) => {
                const eventLectures = lecturesByEvent[event] || [];
                const lectureCount = eventLectures.length;
                
                return (
                  <Link 
                    key={index}
                    to={`/events/${encodeURIComponent(event)}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      border: `1px solid ${theme.colors.lightGray}`,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                      }
                    }}>
                      {/* Event Header */}
                      <div style={{
                        backgroundColor: theme.colors.primary,
                        padding: '20px',
                        color: 'white',
                        textAlign: 'center'
                      }}>
                        <h2 style={{ 
                          margin: 0, 
                          fontSize: '1.5rem',
                          fontFamily: theme.fonts.heading,
                          fontWeight: 'bold'
                        }}>
                          {event}
                        </h2>
                      </div>
                      
                      {/* Event Info */}
                      <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          marginBottom: '15px'
                        }}>
                          <div style={{ 
                            backgroundColor: theme.colors.lightGray,
                            borderRadius: '50%',
                            width: '80px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            color: theme.colors.primary
                          }}>
                            {lectureCount}
                          </div>
                        </div>
                        
                        <div style={{ 
                          textAlign: 'center',
                          color: theme.colors.text,
                          fontFamily: theme.fonts.body,
                          fontSize: '1.1rem',
                          fontWeight: 'bold'
                        }}>
                          {lectureCount === 1 ? '1 Lecture' : `${lectureCount} Lectures`}
                        </div>
                        
                        <div style={{ 
                          marginTop: 'auto',
                          textAlign: 'center',
                          backgroundColor: theme.colors.lightGray,
                          padding: '10px',
                          borderRadius: '5px',
                          color: theme.colors.primary,
                          fontWeight: 'bold',
                          marginTop: '15px'
                        }}>
                          View Event
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          
          {/* No Events */}
          {!loading && !error && events.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h2>No Events Found</h2>
              <p>Check back later for upcoming events.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Floating Navigation Buttons for Mobile */}
      <FloatingNavButtons />
      <FloatingShareButton 
        pageTitle={`${config?.name || 'Penta University'} Lecture Series`}
        pageDescription="Explore our collection of educational lectures and events"
      />
    </Layout>
  );
};

export default HomePage;
